import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Phone, Globe, MessageCircle, Calculator, FolderOpen, MapPin, Bot } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n/LanguageContext";
import { telLink, waLink, COMPANY, trackCallEvent, trackWhatsAppEvent } from "@/lib/api";
import BagnomanLogo from "@/components/BagnomanLogo";
import { openAiChat } from "@/lib/chatEvents";

function NavItems({ onNav }) {
  const { t } = useLang();
  const items = [
    { to: "/", label: t("nav.home") },
    { to: "/servizi", label: t("nav.services") },
    { to: "/attrezzature", label: t("nav.equipment") },
    { to: "/galleria", label: t("nav.gallery") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/recensioni", label: t("nav.reviews") },
    { to: "/chi-siamo", label: t("nav.about") },
    { to: "/contatti", label: t("nav.contact") },
  ];
  return (
    <nav className="flex flex-col gap-0.5" aria-label="Menu principale">
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          onClick={onNav}
          end={i.to === "/"}
          className={({ isActive }) =>
            `flex items-center min-h-[48px] rounded-lg px-3 text-base font-medium transition-colors ${
              isActive ? "bg-[hsl(var(--accent))] text-[hsl(var(--brand-aqua))]" : "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] active:bg-[hsl(var(--muted))]"
            }`
          }
          data-testid={`nav-link-${i.to === "/" ? "home" : i.to.slice(1)}`}
        >
          {i.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** Mobile drawer: emergency CTAs on top (the #1 reason a mobile visitor opens
 * the menu), then quick tools, then the full nav. */
function MobileMenuCTAs({ onNav, lang }) {
  const isIt = lang !== "en";
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="mobile-menu-ctas">
      <a
        href={telLink()}
        onClick={trackCallEvent}
        className="col-span-2 inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-[hsl(var(--brand-aqua))] hover:bg-[hsl(var(--brand-aqua-2))] text-white font-semibold press shadow-sm"
        data-testid="mobile-menu-call-button"
      >
        <Phone className="h-5 w-5" /> {isIt ? "Chiama H24" : "Call 24/7"} · {COMPANY.phone_display}
      </a>
      <a
        href={waLink()}
        onClick={trackWhatsAppEvent}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-[hsl(var(--border))] bg-white text-[hsl(var(--brand-navy))] text-sm font-semibold press"
        data-testid="mobile-menu-whatsapp-button"
      >
        <MessageCircle className="h-4 w-4 text-[hsl(var(--success))]" /> WhatsApp
      </a>
      <button
        type="button"
        onClick={() => {
          onNav();
          openAiChat();
        }}
        className="inline-flex items-center justify-center gap-2 h-11 rounded-xl border border-[hsl(var(--border))] bg-white text-[hsl(var(--brand-navy))] text-sm font-semibold press"
        data-testid="mobile-menu-chat-button"
      >
        <Bot className="h-4 w-4 text-[hsl(var(--brand-aqua))]" /> {isIt ? "Chat AI" : "AI Chat"}
      </button>
    </div>
  );
}

function MobileQuickLinks({ onNav, lang }) {
  const isIt = lang !== "en";
  const links = [
    { to: "/calcolatore", Icon: Calculator, label: isIt ? "Calcolatore spreco" : "Waste calculator" },
    { to: "/casi-studio", Icon: FolderOpen, label: isIt ? "Casi studio" : "Case studies" },
    { to: "/toscana", Icon: MapPin, label: isIt ? "Zone servite" : "Service areas" },
  ];
  return (
    <div className="mt-3 grid grid-cols-3 gap-2" data-testid="mobile-menu-quicklinks">
      {links.map(({ to, Icon, label }) => (
        <Link
          key={to}
          to={to}
          onClick={onNav}
          className="flex flex-col items-center justify-center gap-1 min-h-[64px] rounded-lg bg-[hsl(var(--muted))] px-1 py-2 text-[12px] font-medium text-center leading-tight text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] active:bg-[hsl(var(--accent))] transition-colors"
          data-testid={`mobile-quicklink-${to.slice(1)}`}
        >
          <Icon className="h-4 w-4 text-[hsl(var(--brand-aqua))]" />
          {label}
        </Link>
      ))}
    </div>
  );
}

const DESKTOP_NAV = [
  { to: "/servizi", k: "nav.services" },
  { to: "/attrezzature", k: "nav.equipment" },
  { to: "/galleria", k: "nav.gallery" },
  { to: "/blog", k: "nav.blog" },
  { to: "/recensioni", k: "nav.reviews" },
  { to: "/contatti", k: "nav.contact" },
];

function desktopNavClass({ isActive }) {
  return `px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
    isActive ? "text-[hsl(var(--brand-aqua))] bg-[hsl(var(--accent))]" : "text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]"
  }`;
}

function DesktopNav({ t }) {
  return (
    <nav className="hidden lg:flex items-center gap-1 mr-2" aria-label="Navigazione principale">
      {DESKTOP_NAV.map((i) => (
        <NavLink key={i.to} to={i.to} className={desktopNavClass} data-testid={`nav-link-${i.to.slice(1)}`}>
          {t(i.k)}
        </NavLink>
      ))}
    </nav>
  );
}

function LanguageToggle({ lang, setLang }) {
  return (
    <button
      onClick={() => setLang(lang === "it" ? "en" : "it")}
      className="inline-flex items-center gap-1 rounded-lg h-10 px-2.5 text-xs font-semibold text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] active:bg-[hsl(var(--muted))] transition-colors"
      data-testid="nav-language-toggle"
      aria-label={lang === "it" ? "IT — passa all'inglese" : "EN — switch to Italian"}
    >
      <Globe className="h-4 w-4" aria-hidden="true" />
      {lang === "it" ? "IT" : "EN"}
    </button>
  );
}

function MobileMenuSheet({ open, setOpen, lang }) {
  const close = () => setOpen(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="h-11 w-11 lg:hidden" aria-label="Apri menu" data-testid="nav-open-menu-button">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[min(92vw,360px)] sm:w-[360px] p-4 overflow-y-auto">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="mt-1 mb-4 flex items-center justify-between gap-2">
          <BagnomanLogo size={40} withText />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] px-2.5 py-1 text-[12px] font-semibold mr-8">
            <span className="inline-block h-2 w-2 rounded-full bg-[hsl(var(--success))] animate-pulse" /> H24
          </span>
        </div>
        <MobileMenuCTAs onNav={close} lang={lang} />
        <MobileQuickLinks onNav={close} lang={lang} />
        <div className="my-4 border-t border-[hsl(var(--border))]" />
        <NavItems onNav={close} />
      </SheetContent>
    </Sheet>
  );
}

export function Header() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-[hsl(var(--border))] bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75">
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 h-14 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo-link">
          <BagnomanLogo size={36} withText />
        </Link>

        <div className="ml-auto flex items-center gap-2">
          <DesktopNav t={t} />
          <LanguageToggle lang={lang} setLang={setLang} />
          <a href={telLink()} className="hidden sm:inline-flex" data-testid="nav-call-link">
            <Button size="sm" variant="default" className="h-10 bg-[hsl(var(--brand-aqua))] hover:bg-[hsl(var(--brand-aqua-2))] text-white press">
              <Phone className="h-4 w-4 mr-1.5" />{t("nav.call")}
            </Button>
          </a>
          <MobileMenuSheet open={open} setOpen={setOpen} lang={lang} />
        </div>
      </div>
    </header>
  );
}

export default Header;
